import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Loader2, ChevronLeft, ChevronRight, Plus, Calendar, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { operacionesApi } from '../../lib/api';
import { cn, formatDate } from '../../lib/utils';

// Lun..Sab=1..6, Dom=0 (mismo formato del backend)
const dias_orden = [1, 2, 3, 4, 5, 6, 0];
const dia_label = { 0: 'Dom', 1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb' };

function lunesDe(date) {
  const d = new Date(date);
  const wd = d.getDay(); // 0=Dom..6=Sab
  const diff = wd === 0 ? -6 : 1 - wd;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function isoDate(d) { return d.toISOString().split('T')[0]; }
function fmtFechaCorta(d) {
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

export default function EdificioReservas({ edificioId: edificioIdProp, titulo, subtitulo }) {
  const [salas, setSalas] = useState([]);
  const [salaId, setSalaId] = useState(null);
  const [semana, setSemana] = useState(() => lunesDe(new Date()));
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { fecha, hora_inicio, hora_fin } | null
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSalaId(null);
    operacionesApi.listSalas(edificioIdProp || undefined)
      .then(r => {
        const list = (r.data || []).filter(s => s.activa);
        setSalas(list);
        if (list.length) setSalaId(list[0].sala_id);
        else setLoading(false);
      })
      .catch(() => { toast.error('Error al cargar salas'); setLoading(false); });
  }, [edificioIdProp]);

  const sala = useMemo(() => salas.find(s => s.sala_id === salaId), [salas, salaId]);

  const reload = useCallback(async () => {
    if (!salaId) return;
    setLoading(true);
    try {
      const desde = isoDate(semana);
      const dom = new Date(semana); dom.setDate(dom.getDate() + 6);
      const r = await operacionesApi.listReservas({
        sala_id: salaId, desde, hasta: isoDate(dom),
      });
      setReservas(r.data || []);
    } catch { toast.error('Error al cargar reservas'); }
    finally { setLoading(false); }
  }, [salaId, semana]);

  useEffect(() => { reload(); }, [reload]);

  const semanaCols = useMemo(() => {
    return dias_orden
      .filter(d => sala?.dias_disponibles?.includes(d))
      .map(dow => {
        // semana es lunes; sumar offset
        const offset = dow === 0 ? 6 : dow - 1; // dom=6, lun=0..sab=5
        const d = new Date(semana); d.setDate(d.getDate() + offset);
        return { dow, date: d, iso: isoDate(d) };
      });
  }, [sala, semana]);

  const slots = useMemo(() => {
    if (!sala) return [];
    const [hi, mi] = sala.horario_inicio.split(':').map(Number);
    const [hf, mf] = sala.horario_fin.split(':').map(Number);
    const startMin = hi * 60 + mi;
    const endMin = hf * 60 + mf;
    const out = [];
    for (let m = startMin; m + sala.granularidad_min <= endMin; m += sala.granularidad_min) {
      out.push({
        from: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`,
        to: `${String(Math.floor((m + sala.granularidad_min) / 60)).padStart(2, '0')}:${String((m + sala.granularidad_min) % 60).padStart(2, '0')}`,
      });
    }
    return out;
  }, [sala]);

  // Index reservas por fecha+hora_inicio
  const idx = useMemo(() => {
    const m = new Map();
    reservas.forEach(r => {
      const key = `${r.fecha}|${r.hora_inicio}`;
      m.set(key, r);
    });
    return m;
  }, [reservas]);

  const handleClickSlot = (col, slot) => {
    const r = idx.get(`${col.iso}|${slot.from}`);
    if (r) {
      // Slot ocupado — proponer cancelar si es propia
      setDialog({ mode: 'cancel', reserva: r });
    } else {
      setDialog({
        mode: 'new',
        fecha: col.iso,
        hora_inicio: slot.from,
        hora_fin: slot.to,
        notas: '',
      });
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await operacionesApi.crearReserva({
        sala_id: salaId,
        fecha: dialog.fecha,
        hora_inicio: dialog.hora_inicio,
        hora_fin: dialog.hora_fin,
        notas: dialog.notas,
      });
      toast.success('Reserva confirmada');
      setDialog(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reservar');
    } finally { setSaving(false); }
  };

  const handleCancel = async () => {
    setSaving(true);
    try {
      await operacionesApi.cancelarReserva(dialog.reserva.reserva_id);
      toast.success('Reserva cancelada');
      setDialog(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cancelar');
    } finally { setSaving(false); }
  };

  const cambiarSemana = (delta) => {
    const d = new Date(semana); d.setDate(d.getDate() + 7 * delta);
    setSemana(d);
  };

  if (!salas.length && !loading) {
    return <div className="p-8 text-center text-slate-500">Aún no hay salas configuradas en este edificio.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-indigo-700" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">{titulo || 'Reservas de salas'}</h1>
          <p className="text-xs text-slate-500">{subtitulo || 'Click en un slot libre para reservar.'}</p>
        </div>
      </div>

      {/* Controles */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select className="border border-slate-200 rounded h-9 px-2 text-sm font-medium"
          value={salaId || ''}
          onChange={e => setSalaId(e.target.value)}
          data-testid="select-sala">
          {salas.map(s => (
            <option key={s.sala_id} value={s.sala_id}>{s.nombre} (cap {s.capacidad})</option>
          ))}
        </select>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => cambiarSemana(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-9 px-2 text-xs" onClick={() => setSemana(lunesDe(new Date()))}>
            Hoy
          </Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => cambiarSemana(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {sala && sala.descripcion && (
        <div className="text-xs text-slate-500 mb-2">{sala.descripcion}</div>
      )}

      {/* Grilla */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto relative">
        {loading && <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-2 py-1.5 text-xs font-medium text-slate-500 w-[80px]">Hora</th>
              {semanaCols.map(col => (
                <th key={col.iso} className="text-left px-2 py-1.5 text-xs font-medium">
                  <div className="text-slate-700">{dia_label[col.dow]}</div>
                  <div className="text-[10px] text-slate-400">{fmtFechaCorta(col.date)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {slots.map(slot => (
              <tr key={slot.from}>
                <td className="px-2 py-1 text-[11px] text-slate-500 font-mono whitespace-nowrap">
                  {slot.from}–{slot.to}
                </td>
                {semanaCols.map(col => {
                  const r = idx.get(`${col.iso}|${slot.from}`);
                  return (
                    <td key={col.iso} className="p-0.5">
                      <button
                        type="button"
                        onClick={() => handleClickSlot(col, slot)}
                        className={cn(
                          'w-full h-9 rounded text-[11px] px-1.5 transition border text-left truncate',
                          r
                            ? 'bg-indigo-100 border-indigo-300 text-indigo-900 hover:bg-indigo-200'
                            : 'bg-emerald-50/30 border-dashed border-slate-200 hover:bg-emerald-100 hover:border-emerald-400 text-slate-400 hover:text-emerald-800'
                        )}
                        data-testid={`slot-${col.iso}-${slot.from}`}
                      >
                        {r ? (
                          <span className="font-medium truncate block">{r.usuario_nombre}</span>
                        ) : (
                          <span><Plus className="w-3 h-3 inline" /></span>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog crear / cancelar */}
      <Dialog open={!!dialog} onOpenChange={o => !o && setDialog(null)}>
        <DialogContent className="max-w-md">
          {dialog?.mode === 'new' && (
            <>
              <DialogHeader><DialogTitle>Reservar {sala?.nombre}</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                <div><span className="text-slate-500">Fecha:</span> <b>{formatDate(dialog.fecha)}</b></div>
                <div><span className="text-slate-500">Horario:</span> <b>{dialog.hora_inicio} – {dialog.hora_fin}</b></div>
                <div>
                  <Label>Notas (opcional)</Label>
                  <Input value={dialog.notas} onChange={e => setDialog({ ...dialog, notas: e.target.value })} placeholder="Reunión con cliente, etc." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
                <Button onClick={handleCreate} disabled={saving} data-testid="btn-confirm-reserva">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirmar'}
                </Button>
              </DialogFooter>
            </>
          )}
          {dialog?.mode === 'cancel' && (
            <>
              <DialogHeader><DialogTitle>{dialog.reserva.usuario_nombre}</DialogTitle></DialogHeader>
              <div className="space-y-2 py-2 text-sm">
                <div>{formatDate(dialog.reserva.fecha)} · {dialog.reserva.hora_inicio}–{dialog.reserva.hora_fin}</div>
                {dialog.reserva.notas && <div className="text-slate-600 italic">"{dialog.reserva.notas}"</div>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)}>Cerrar</Button>
                <Button variant="destructive" onClick={handleCancel} disabled={saving} data-testid="btn-cancel-reserva">
                  <X className="w-3 h-3 mr-1" /> {saving ? 'Cancelando...' : 'Cancelar reserva'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
